<template>
  <div class="slideLayout" :class="classesTemplate" :ref="'slideContainerPlayer'"
  :style="maxHeightStyle">
    <!-- IMAGE SLIDE -->
    <div v-if="media.type==='SLIDE'" class="slideExportStaticContainer" :style="'background-image: url('+urlExportStaticUrl+');'">
    </div>
    <div v-else class="coloredBackground" :class="classesTemplate" :style="'border-bottom: 8px solid '+theme ">
      <!-- VIDEO BACKGROUND -->
      <VideoImageBackground :animate="isPlaying" v-if="isVideoBackground" :url="videoUrl||slideImage" :mask="getMask" />

      <!-- TITLE -->
      <div class="titleElement" :class="classesTemplate">
        <transition :name="animationTitleName">
          <div v-html="slideTitle" :ref="'title-container-'+id"
               class="titleTxt" v-show="showTitle"
               :style="'color:'+theme+'; font-size:'+titleFontSize+'px;animation-duration:'+animationTitleDuration+';'"
          />
        </transition>
      </div>

      <!-- DESCRIPTION -->
      <div class="descriptionElement" :class="classesTemplate">
        <transition :name="animationDescriptionName">
          <div v-html="slideDescription" :ref="'description-container-'+id"
               class="descriptionTxt" v-show="showDescription"
               :style="'border-left: 2px solid '+theme +'; font-size:'+descriptionFontSize+'px;animation-delay:'+animationDescriptionDelay+';animation-duration:'+animationDescriptionDuration+';'"
          />
        </transition>
      </div>
    </div>
    <div class="videoLayout" :class="classesTemplate">
      <VideoImageBackground :animate="isPlaying" :url="videoUrl||slideImage" />
    </div>

    <!-- LOGO -->
    <div class="logoElement" :class="classesTemplate">
      <transition :name="'fade'">
        <img v-show="showTitle" class="logoWorkspace" :src="logoSlide" :style="'height:'+logoHeightSize+'px;' "/>
      </transition>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { Media } from "@/store/media/mediaModel";
import { getImageURLForSize, htmliFy } from "@/utils/util";
import debounce from 'lodash/debounce';
import store from "@/store";
import VideoImageBackground from "@/components/videoImageBackground/VideoImageBackground.vue";
import { SlideTemplate } from "@/store/slides2Video/slides2VideoModels";
import {
  DESCRIPTION_FONT_RATIO,
  LOGO_HEIGHT_RATIO,
  TITLE_FONT_RATIO
} from "@/components/recorder/aiAvatar/panel/AISlideConstant";
import { isSpeach } from "@/utils/speachUtils";


@Component({
  components: { VideoImageBackground }
})
export default class SlidePlayerLayout extends Vue {



  @Prop({ required: true })
  private media!: Media;

  @Prop({ required: true })
  private id!: string;

  @Prop({ required: true })
  private isShow!: boolean;

  @Prop({default: 0})
  private maxHeight!: number;

  private slideContainerWidth:number=0;
  private resizeObserverSlideContainer!: ResizeObserver;

  private showTitle: boolean = false;
  private showDescription: boolean = false;

  private shouldAnimate=true;

  private debouceOnSeek = debounce(() => {
    this.shouldAnimate=true;
  }, 200);


  get classesTemplate() {
    if(this.media.type==='SLIDE') return "";
    return this.media.slideTemplate;
  }

  get urlExportStaticUrl(){
    return getImageURLForSize(this.media.exportStaticUrl,1080);
  }

  get isVideoBackground(){
    return this.media.slideTemplate===SlideTemplate.TITLE_ILLUSTRATED || this.media.slideTemplate===SlideTemplate.ILLUSTRATED_ONLY;
  }

  get slideTitle() {
    return htmliFy(this.media.title || "");
  }

  get slideDescription() {
    return htmliFy(this.media.description || "");
  }

  get theme() {
    return this.media.theme || "#7957D5";
  }

  get slideImage(){
    return getImageURLForSize(this.media.slideImageUrl,1920)
  }
  get videoUrl(){
    return this.media.cdnMediaUrl;
  }

  get titleFontSize(){
    return this.slideContainerWidth/TITLE_FONT_RATIO;
  }
  get descriptionFontSize(){
    return this.slideContainerWidth/DESCRIPTION_FONT_RATIO;
  }

  get logoHeightSize(){
    return this.slideContainerWidth/LOGO_HEIGHT_RATIO;
  }

  get animationTitleName(){
    switch (this.media.slideTemplate) {
      case "AVATAR_LEFT":
        return "fadeRight";
      case "AVATAR_RIGHT":
        return "fadeLeft";
      case "ILLUSTRATED_LEFT":
        return "fadeRight";
      case "ILLUSTRATED_RIGHT":
        return "fadeLeft";
      case "TITLE":
        return "fadeDown";
      case "TITLE_ILLUSTRATED":
        return "fadeDown";
    }
    return "fade"
  }
  get animationDescriptionName(){
    switch (this.media.slideTemplate) {
      case "TITLE":
        return "fadeUp";
      case "TITLE_ILLUSTRATED":
        return "fadeUp";
    }
    return this.animationTitleName
  }
  get animationDescriptionDelay(){
    return this.shouldAnimate && this.isPlaying?"2s":"0s"
  }
  get animationDescriptionDuration(){
    return this.shouldAnimate&& this.isPlaying?"2s":"0s"
  }

  get animationTitleDuration(){
    return this.shouldAnimate&& this.isPlaying?"1s":"0s"
  }

  get isPlaying(){
    return store.getters.isPlaying;
  }

  get isFullscreenMode(): boolean {
    return store.getters.isFullscreenMode;
  }

  get getMask(){
    if(this.media.slideTemplate!==SlideTemplate.ILLUSTRATED_ONLY) {
      return 'light'
    }else{
      return '';
    }
  }

  get maxHeightStyle(){
    if(this.maxHeight>0) {
      return "width:min(100%, " + this.maxHeight + "px * (16 / 9))";
    }else{
      return ""
    }
  }

  get logoSlide(){
    if(isSpeach()){
      return  store.getters.getSpeachLogo;
    }else {
      return getImageURLForSize(store.getters.getSelectedWorkspace?.imageURL, 160);
    }
  }

  /**
   * Call when a user seek in the timeline
   * @param mediaTime
   */
  public seeked(mediaTime) {
    if(mediaTime>2000) {
      this.shouldAnimate = false;
      this.debouceOnSeek();
    }else{
      this.shouldAnimate = true;
    }
  }

  @Watch("isShow")
  private onShowChange() {
    setTimeout(()=>{
      this.launchAnimation()
    },100)
  }
  @Watch("media")
  private onMediaChange() {
    this.initObserver()
    this.$emit("ready");

  }

  private launchAnimation(){
    if(this.media.type==='SLIDE') return;
    this.showTitle = false;
    this.showDescription = false;
    this.$nextTick(() => {
      this.showTitle = true;
      this.showDescription = true;
    })
  }

  mounted() {
    this.$emit("ready");
    this.initObserver()
    // launch animation
    this.launchAnimation()
  }


  private initObserver(){
    const el = this.$refs["slideContainerPlayer"] as HTMLDivElement;
    // Nettoyage observer
    if (this.resizeObserverSlideContainer) {
      this.resizeObserverSlideContainer.disconnect();
    }
    this.resizeObserverSlideContainer = new ResizeObserver(entries => {
      for (const entry of entries) {
        this.slideContainerWidth = entry.contentRect.width;
      }
    });
    this.resizeObserverSlideContainer.observe(el);
  }
  beforeDestroy() {
    // Nettoyage observer
    if (this.resizeObserverSlideContainer) {
      this.resizeObserverSlideContainer.disconnect();
    }
  }
}
</script>

<style lang="scss" scoped>

.slideLayout {
  aspect-ratio: 16/9;
  background: white;
  grid-row-start: 1;
  grid-column-start: 1;
  position: relative;
  width: min(100%, 100vh * (16 / 9));
  height: auto;
  max-height: 100vh;

  .slideExportStaticContainer{
    width: 100%;
    height: 100%;
    background-color: var(--black);
    background-size: contain;
    background-position: center center;
    background-repeat: no-repeat;
    border-radius: 8px;
  }
  .logoElement{
    position: absolute;
    bottom: 12px;
    width: calc(100% - 16px);
    display: flex;
    right: 16px;
    align-items: flex-end;
    .logoWorkspace{
      height: 100%;
      animation-delay: 1s;
      object-fit: contain;
      margin-left: auto;
    }
  }
  .coloredBackground {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    right: 0;
    display: flex;
    background: white;
    flex-direction: column;

    .titleElement {
      position: relative;
      font-weight: 700;
      max-height: 33%;
      display: flex;
      justify-content: center;
      align-items: center;
      margin-top: 4%;
      margin-bottom: 4%;

      .titleTxt {
        margin-left: 4%;
        margin-right: 4%;
        padding-left: 4%;
        padding-right: 4%;
        font-weight: 900;
      }

      &.ILLUSTRATED_ONLY{
        display: none;
      }
    }

    .descriptionElement {
      position: relative;
      margin-top: 0;
      margin-bottom: 4%;
      display: flex;
      align-items: center;
      justify-content: center;
      flex: 1;
      max-height: 67%;
      color: var(--black);

      .descriptionTxt {
        margin-left: 4%;
        margin-right: 4%;
        padding-left: 4%;
        padding-right: 4%;
        text-align: left;

        ::v-deep strong, ::v-deep b {
          color: inherit;
          font-weight: 900;
        }
      }
      &.ILLUSTRATED_ONLY{
        display: none;
      }
    }

  }


  .videoLayout{
    position: absolute;
    top:0;
    height: 100%;
    display: none;
    &.ILLUSTRATED_LEFT{
      display: block;
      width: 40%;
      left: 0px;
    }
    &.ILLUSTRATED_RIGHT{
      display: block;
      width: 40%;
      right: 0px;
    }
  }
  // TEMPLATE AND THEME DEFINITION
  .coloredBackground {
    &.AVATAR_LEFT, &.ILLUSTRATED_LEFT {
      width: 60%;
      right: 0;
    }

    &.AVATAR_RIGHT,&.ILLUSTRATED_RIGHT {
      width: 60%;
      left: 0;
    }
  }
}
</style>
